import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { ShellData } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Check, PackageCheck, PackageSearch, Truck, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api-client'
import { toLocale, useI18n } from '@/lib/i18n'
import { formatDateTime } from '@/lib/format'
import { ORDER_STATUS, toOrderStatus } from '@/constants/order-status'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/field'
import { StoreLayout } from '@/components/store/store-layout'
import { PageHeader } from '@/components/store/page-header'
import { CmsText } from '@/components/store/cms-text'
import { Num, Price } from '@/components/store/price'
import { loadShell } from '@/lib/server/store'

interface TrackPageProps {
    shell: ShellData
}

interface TrackResult {
    order: {
        id: string
        number: string
        status: string
        subtotal: number
        discount: number
        shipping: number
        total: number
        itemsCount: number
        governorate: string
        city: string
        createdAt: string
        updatedAt: string
    }
    items: Array<{ id: string, name: string, nameColumn: string, variant: string, variantColumn: string, quantity: number, total: number }>
}

const STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'] as const

const TrackPage: FC<TrackPageProps> = ({ shell }): ReactNode => {
    const { tx, locale } = useI18n()
    const router = useRouter()
    const [number, setNumber] = useState('')
    const [phone, setPhone] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<TrackResult | null>(null)

    useEffect(() => {
        const value = router.query.number

        if (typeof value === 'string') setNumber(value)
    }, [router.query.number])

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()
        setError(null)
        setLoading(true)

        try {
            setResult(await api<TrackResult>('/api/orders/track', { body: { number, phone }, locale }))
        }
        catch (caught) {
            setResult(null)
            setError(caught instanceof Error ? caught.message : tx('حدث خطأ، حاول مرة أخرى.', 'Something went wrong. Please try again.'))
        }
        finally {
            setLoading(false)
        }
    }

    const status = result ? toOrderStatus(result.order.status) : null
    const cancelled = status === 'cancelled'
    const currentIndex = status && !cancelled ? STEPS.indexOf(status as typeof STEPS[number]) : -1

    return (
        <StoreLayout shell={shell}>
            <Seo title={tx('تتبع طلبك', 'Track your order')} description={tx('تابع حالة طلبك برقم الطلب ورقم الموبايل.', 'Check your order status with your order number and mobile number.')} siteName={shell.settings.storeName.v} origin={shell.origin} path='/track' />

            <PageHeader
                title={tx('تتبع طلبك', 'Track your order')}
                subtitle={tx('أدخل رقم الطلب الموجود في صفحة التأكيد ورقم الموبايل المستخدم في الطلب.', 'Enter the order number from your confirmation page and the mobile number used for the order.')}
                crumbs={[{ label: tx('تتبع الطلب', 'Track order') }]}
            />

            <div className='bg-surface'>
                <div className='container-page grid items-start gap-6 py-12 sm:py-16 lg:grid-cols-12'>
                    <form onSubmit={submit} className='flex flex-col gap-5 rounded-3xl bg-white p-6 ring-1 ring-navy-100 sm:p-8 lg:col-span-5'>
                        <span className='flex size-12 items-center justify-center rounded-2xl bg-navy-900 text-cyan-300'>
                            <PackageSearch className='size-6' aria-hidden='true' />
                        </span>

                        <Field label={tx('رقم الطلب', 'Order number')} htmlFor='track-number' required>
                            <Input id='track-number' dir='ltr' className='text-start' inputMode='numeric' placeholder='261104-1234' value={number} onChange={event => setNumber(event.target.value)} required />
                        </Field>

                        <Field label={tx('رقم الموبايل', 'Mobile number')} htmlFor='track-phone' required>
                            <Input id='track-phone' type='tel' dir='ltr' className='text-start' inputMode='tel' placeholder='01012345678' value={phone} onChange={event => setPhone(event.target.value)} required />
                        </Field>

                        {error && <p className='rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-100' role='alert'>{error}</p>}

                        <Button type='submit' size='lg' loading={loading}>{tx('اعرض حالة الطلب', 'Show order status')}</Button>
                    </form>

                    <div className='lg:col-span-7'>
                        {!result && (
                            <div className='flex h-full min-h-64 flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-navy-100 p-8 text-center'>
                                <Truck className='size-8 text-navy-300' strokeWidth={1.5} aria-hidden='true' />
                                <p className='max-w-sm text-sm leading-relaxed text-muted-foreground'>{tx('ستظهر حالة طلبك وتفاصيله هنا.', 'Your order status and details will appear here.')}</p>
                            </div>
                        )}

                        {result && status && (
                            <div className='flex flex-col gap-6 rounded-3xl bg-white p-6 ring-1 ring-navy-100 sm:p-8 animate-in fade-in slide-in-from-bottom-2 duration-500'>
                                <div className='flex flex-wrap items-start justify-between gap-3'>
                                    <div className='flex flex-col gap-1'>
                                        <span className='text-sm text-muted-foreground'>{tx('طلب رقم', 'Order')}</span>
                                        <span className='text-xl font-bold tabular-nums text-navy-950' dir='ltr'>{result.order.number}</span>
                                    </div>
                                    <span className='text-xs text-muted-foreground'>{formatDateTime(result.order.createdAt, locale)}</span>
                                </div>

                                {cancelled
                                    ? (
                                        <div className='flex items-center gap-3 rounded-2xl bg-red-50 p-4 text-red-700 ring-1 ring-red-100'>
                                            <XCircle className='size-6 shrink-0' aria-hidden='true' />
                                            <p className='text-sm font-medium'>{tx('تم إلغاء هذا الطلب. تواصل معنا لأي استفسار.', 'This order was cancelled. Contact us with any questions.')}</p>
                                        </div>
                                    )
                                    : (
                                        <ol className='grid grid-cols-5 gap-2'>
                                            {STEPS.map((step, index) => {
                                                const done = index <= currentIndex

                                                return (
                                                    <li key={step} className='flex flex-col items-center gap-2 text-center'>
                                                        <span className={cn('flex size-10 items-center justify-center rounded-full transition-colors', done ? 'bg-cyan-500 text-navy-950' : 'bg-navy-50 text-navy-300', index === currentIndex && 'ring-4 ring-cyan-200')}>
                                                            {index === STEPS.length - 1 ? <PackageCheck className='size-5' aria-hidden='true' /> : <Check className='size-5' aria-hidden='true' />}
                                                        </span>
                                                        <span className={cn('text-[11px] font-medium leading-tight sm:text-xs', done ? 'text-navy-950' : 'text-navy-400')}>{locale === 'en' ? ORDER_STATUS[step].en : ORDER_STATUS[step].ar}</span>
                                                    </li>
                                                )
                                            })}
                                        </ol>
                                    )}

                                <ul className='divide-y divide-navy-100 rounded-2xl bg-surface px-4'>
                                    {result.items.map(item => (
                                        <li key={item.id} className='flex items-center justify-between gap-3 py-3 text-sm'>
                                            <span className='flex flex-col'>
                                                <CmsText table='order_items' id={item.id} field={{ v: item.name, c: item.nameColumn }} className='font-medium text-navy-950' />
                                                {item.variant && <CmsText table='order_items' id={item.id} field={{ v: item.variant, c: item.variantColumn }} className='text-xs text-muted-foreground' />}
                                            </span>
                                            <span className='flex items-center gap-3'>
                                                <span className='text-xs text-muted-foreground'>
                                                    {'× '}
                                                    <Num value={item.quantity} />
                                                </span>
                                                <Price value={item.total} className='font-semibold text-navy-950' />
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <div className='flex items-center justify-between border-t border-navy-100 pt-4'>
                                    <span className='text-sm text-navy-700'>
                                        {tx('الشحن إلى', 'Shipping to')}
                                        {' '}
                                        <CmsText table='orders' id={result.order.id} field={{ v: result.order.governorate, c: 'governorate_name' }} className='font-medium text-navy-950' />
                                    </span>
                                    <span className='flex items-baseline gap-2'>
                                        <span className='text-sm text-muted-foreground'>{tx('الإجمالي', 'Total')}</span>
                                        <Price value={result.order.total} className='text-lg font-bold text-navy-950' />
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </StoreLayout>
    )
}

export const getServerSideProps: GetServerSideProps<TrackPageProps> = async ({ locale: rawLocale, req }) => {
    const shell = await loadShell(toLocale(rawLocale), req)

    return { props: { shell } }
}

export default TrackPage
